import React, {useEffect, useState} from 'react';
import type {MenuProps} from 'antd';
import {Layout, Menu, Typography} from 'antd';
import {
    AppleFilled,
    ClockCircleOutlined,
    FileTextOutlined,
    FireOutlined,
    FolderOutlined,
    StarOutlined,
} from '@ant-design/icons';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '../../contexts/AuthContext';
import {useTheme} from '../ThemeProvider';
import TagGroupService, {TagGroup} from '../../service/tagGroupService';
import GooglePlayIcon from '../Icons/GooglePlayIcon';
import {APP_LINKS, USE_USER} from '../../config/features';
import logo from '../../assets/logo.png';
import './Sidebar.css';

const { Sider } = Layout;
const { Text } = Typography;

interface SidebarProps {
  collapsed: boolean;
  screens?: any;
  onMenuClick?: () => void;
}

type MenuItem = Required<MenuProps>['items'][number];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, screens, onMenuClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const { isDark } = useTheme();
  const isMobile = screens && !screens.md;
  const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);

  useEffect(() => {
    if (USE_USER && token) {
      fetchTagGroups();
    }
  }, [token]);

  // Listen for tag group updates
  useEffect(() => {
    const handleTagGroupsUpdate = () => {
      if (USE_USER && token) {
        fetchTagGroups();
      }
    };

    window.addEventListener('tagGroupsUpdated', handleTagGroupsUpdate);

    return () => {
      window.removeEventListener('tagGroupsUpdated', handleTagGroupsUpdate);
    };
  }, [token]);

  const fetchTagGroups = async () => {
    try {
      const response = await TagGroupService.getUserTagGroups();
      setTagGroups(response.tag_groups);
    } catch (error) {
      setTagGroups([]);
    }
  };

  const handleMenuClick = (key: string) => {
    navigate(key);
    // Close sidebar on mobile after navigation
    if (isMobile && onMenuClick) {
      onMenuClick();
    }
  };

  // Build menu items
  const menuItems: MenuItem[] = [
    {
      key: '/',
      icon: <FileTextOutlined />,
      label: 'Recent News',
      onClick: () => handleMenuClick('/'),
    },
    {
      key: '/trending',
      icon: <FireOutlined />,
      label: 'Trending News',
      onClick: () => handleMenuClick('/trending'),
    },
  ];

  // Add authenticated user menu items only when USE_USER is enabled
  if (USE_USER && token) {
    menuItems.push(
      {
        type: 'divider',
      },
      {
        key: 'user-section',
        label: collapsed ? null : 'My Content',
        type: 'group',
      }
    );

    // Add Personalized menu with conditional structure based on tag groups
    if (tagGroups && tagGroups.length > 0) {
      // Has tag groups - show as submenu
      menuItems.push({
        key: '/personalized',
        icon: <StarOutlined />,
        label: 'Personalized',
        children: tagGroups.map(group => ({
          key: `/personalized/${group.id}`,
          icon: <FolderOutlined />,
          label: group.name,
          onClick: () => handleMenuClick(`/personalized/${group.id}`),
        })),
      });
    } else {
      // No tag groups - show as simple menu item
      menuItems.push({
        key: '/personalized',
        icon: <StarOutlined />,
        label: 'Personalized',
        onClick: () => handleMenuClick('/personalized'),
      });
    }

    // Add Read Later menu item
    menuItems.push({
      key: '/read-later',
      icon: <ClockCircleOutlined />,
      label: 'Read Later',
      onClick: () => handleMenuClick('/read-later'),
    });
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      breakpoint="md"
      collapsedWidth={isMobile ? 0 : 80}
      width={240}
      className="app-sidebar"
      theme={isDark ? 'dark' : 'light'}
      style={{
        overflow: 'hidden',
        height: '100vh',
        position: 'fixed',
        left: isMobile ? (collapsed ? '-240px' : '0') : '0',
        top: 0,
        bottom: 0,
        background: isDark ? '#001529' : '#fff',
        zIndex: isMobile ? 1000 : 'auto',
        transition: 'left 0.3s ease',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
      <div
        className="sidebar-logo"
        onClick={() => handleMenuClick('/')}
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '16px' : '16px 20px',
          borderBottom: isDark ? '1px solid #1f1f1f' : '1px solid #f0f0f0',
          gap: '12px',
        }}
      >
        <img
          src={logo}
          alt="News Logo"
          style={{
            height: '40px',
            width: '40px',
            objectFit: 'contain',
            transition: 'all 0.3s ease',
          }}
        />
        {!collapsed && (
          <span
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: isDark ? '#fff' : '#000',
              whiteSpace: 'nowrap',
              transition: 'all 0.3s ease',
            }}
          >
            metagame.news
          </span>
        )}
      </div>

      <Menu
        theme={isDark ? 'dark' : 'light'}
        mode="inline"
        selectedKeys={[location.pathname]}
        defaultOpenKeys={USE_USER ? ['/personalized'] : []}
        items={menuItems}
        style={{
          borderRight: 0,
        }}
      />

      {/* App Download Links — right under menu */}
      {!USE_USER && !collapsed && (
        <div
          style={{
            padding: '12px 20px',
            borderTop: isDark ? '1px solid #1f1f1f' : '1px solid #f0f0f0',
          }}
        >
          <Text
            style={{
              fontSize: '11px',
              color: isDark ? '#595959' : '#8c8c8c',
              display: 'block',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Get the App
          </Text>
          <div style={{ display: 'flex', gap: '8px' }}>
            <a
              href={APP_LINKS.ios}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '6px',
                background: isDark ? '#1f1f1f' : '#f5f5f5',
                color: isDark ? '#d9d9d9' : '#434343',
                textDecoration: 'none',
                fontSize: '12px',
              }}
            >
              <AppleFilled style={{ fontSize: '14px' }} />
              iOS
            </a>
            <a
              href={APP_LINKS.android}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '6px',
                background: isDark ? '#1f1f1f' : '#f5f5f5',
                color: isDark ? '#d9d9d9' : '#434343',
                textDecoration: 'none',
                fontSize: '12px',
              }}
            >
              <GooglePlayIcon style={{ fontSize: '14px' }} />
              Android
            </a>
          </div>
        </div>
      )}

      {/* Spacer to push contact/privacy down */}
      <div style={{ flex: 1 }} />

      {/* Contact & Privacy — pinned near bottom */}
      {!collapsed && (
        <div
          style={{
            padding: '10px 20px',
            paddingBottom: '80px',
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <span
            onClick={() => handleMenuClick('/contact')}
            style={{
              fontSize: '11px',
              color: isDark ? '#595959' : '#8c8c8c',
              cursor: 'pointer',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = isDark ? '#a6a6a6' : '#434343')}
            onMouseLeave={(e) => (e.currentTarget.style.color = isDark ? '#595959' : '#8c8c8c')}
          >
            Contact
          </span>
          <span style={{ color: isDark ? '#333' : '#d9d9d9', fontSize: '11px' }}>·</span>
          <span
            onClick={() => handleMenuClick('/privacy-policy')}
            style={{
              fontSize: '11px',
              color: isDark ? '#595959' : '#8c8c8c',
              cursor: 'pointer',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = isDark ? '#a6a6a6' : '#434343')}
            onMouseLeave={(e) => (e.currentTarget.style.color = isDark ? '#595959' : '#8c8c8c')}
          >
            Privacy Policy
          </span>
        </div>
      )}
      </div>
    </Sider>
  );
};

export default Sidebar;
